<template>
    <v-dialog
        :value="value"
        width="1040"
        :fullscreen="$vuetify.breakpoint.smAndDown"
        content-class="hf-ticket-dialog__overlay"
        @input="$emit('input', $event)"
    >
        <article class="hf-ticket-dialog" aria-labelledby="hf-ticket-dialog-title">
            <header class="hf-ticket-dialog__intro">
                <span class="hf-ticket-dialog__mark" aria-hidden="true">
                    <v-icon>{{ mdiLifebuoy }}</v-icon>
                </span>
                <div class="hf-ticket-dialog__copy">
                    <span class="hf-ticket-dialog__eyebrow">ORGANIZATION SUPPORT / HAPPYFOX</span>
                    <h2 id="hf-ticket-dialog-title">Ticket {{ ticketNumber }}</h2>
                    <p>Review the request, assigned agent, and ticket timeline.</p>
                </div>
                <button
                    type="button"
                    class="hf-ticket-dialog__close"
                    aria-label="Close support ticket details"
                    @click="close"
                >
                    <v-icon aria-hidden="true">{{ mdiClose }}</v-icon>
                </button>
            </header>

            <section class="hf-ticket-dialog__hero" aria-label="Ticket summary">
                <div class="hf-ticket-dialog__copy">
                    <span class="hf-ticket-dialog__eyebrow">SUPPORT REQUEST</span>
                    <h3>{{ text(safeTicket.subject, 'Untitled ticket') }}</h3>
                    <p>Created {{ formatDate(safeTicket.createdat) }}</p>
                </div>
                <span
                    class="hf-ticket-dialog__status"
                    :class="`hf-ticket-dialog__status--${statusTone(safeTicket.status)}`"
                >
                    {{ text(safeTicket.status, 'Unknown') }}
                </span>
            </section>

            <div class="hf-ticket-dialog__scroll" tabindex="0" aria-label="Ticket information">
                <dl class="hf-ticket-dialog__facts">
                    <div v-for="fact in detailFacts" :key="fact.label" class="hf-ticket-dialog__fact">
                        <dt>{{ fact.label }}</dt>
                        <dd>{{ fact.value }}</dd>
                    </div>
                </dl>
                <section class="hf-ticket-dialog__message" aria-labelledby="hf-ticket-message-title">
                    <span class="hf-ticket-dialog__eyebrow">MESSAGES</span>
                    <p>{{ initialMessage }}</p>
                </section>
            </div>

            <footer class="hf-ticket-dialog__footer">
                <span>Ticket {{ ticketNumber }}</span>
                <button type="button" class="hf-ticket-dialog__action" @click="close">Close details</button>
            </footer>
        </article>
    </v-dialog>
</template>

<script>
import Vue from 'vue'
import { mdiClose, mdiLifebuoy } from '@mdi/js'
import { ticketText, formatTicketDate, ticketStatusTone } from '@/utils/happyfox_tickets'

export default Vue.extend({
    name: 'HappyfoxTicketDetailsDialog',
    props: {
        value: { type: Boolean, default: false },
        ticket: { type: Object, default: () => ({}) }
    },
    data() {
        return { mdiClose, mdiLifebuoy }
    },
    computed: {
        safeTicket() {
            return this.ticket || {}
        },
        ticketNumber() {
            const id = ticketText(this.safeTicket.ticketId)
            return id === '—' || id.startsWith('#') ? id : `#${id}`
        },
        initialMessage() {
            const message = this.safeTicket.firstmessage
            return typeof message === 'string' && message.trim() ? message : 'No initial message is available.'
        },
        detailFacts() {
            const ticket = this.safeTicket
            return [
                { label: 'Ticket ID', value: ticketText(ticket.ticketId) },
                { label: 'Status', value: ticketText(ticket.status, 'Unknown') },
                { label: 'Requester', value: ticketText(ticket.create_by) },
                { label: 'Requester email', value: ticketText(ticket.create_by_email) },
                { label: 'Assigned agent', value: ticketText(ticket.assigned_to) },
                { label: 'Agent email', value: ticketText(ticket.assigned_to_email) },
                { label: 'Created', value: formatTicketDate(ticket.createdat) },
                { label: 'Closed', value: formatTicketDate(ticket.closedat) },
                { label: 'Last agent reply', value: formatTicketDate(ticket.lastagentreplyat) }
            ]
        }
    },
    methods: {
        text: ticketText,
        formatDate: formatTicketDate,
        statusTone: ticketStatusTone,
        close() {
            this.$emit('input', false)
        }
    }
})
</script>

<style>
/* Global, uniquely prefixed rules also reach Vuetify's detached dialog portal. */
.hf-ticket-dialog__overlay.v-dialog {
    height: calc(100vh - 32px);
    max-height: calc(100vh - 32px) !important;
    margin: 16px;
    overflow: hidden;
}
.hf-ticket-dialog {
    display: flex;
    height: 100%;
    flex-direction: column;
    overflow: hidden;
    color: #f1f2f5;
    background: #282a32;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 10px;
    box-shadow: 0 22px 65px rgba(0, 0, 0, 0.42);
    font-family: 'Work Sans', 'Roboto', sans-serif;
    overflow-wrap: anywhere;
}
.hf-ticket-dialog__intro,
.hf-ticket-dialog__hero,
.hf-ticket-dialog__footer {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 16px;
    padding: 21px 25px;
}
.hf-ticket-dialog__intro {
    background: linear-gradient(115deg, rgba(219, 81, 32, 0.16), transparent);
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.hf-ticket-dialog__mark {
    display: grid;
    width: 42px;
    height: 42px;
    flex: 0 0 42px;
    background: #db5120;
    border-radius: 10px;
    place-items: center;
}
.hf-ticket-dialog .v-icon {
    color: inherit;
}
.hf-ticket-dialog__copy {
    min-width: 0;
    flex: 1;
}
.hf-ticket-dialog__eyebrow {
    display: block;
    margin-bottom: 5px;
    color: #f2a58b;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.2px;
}
.hf-ticket-dialog h2,
.hf-ticket-dialog h3 {
    margin: 0;
    color: #fff;
    font-weight: 600;
}
.hf-ticket-dialog h2 {
    font-size: 23px;
}
.hf-ticket-dialog h3 {
    font-size: 16px;
}
.hf-ticket-dialog__copy p {
    margin: 5px 0 0;
    color: #c6c8d0;
    font-size: 12px;
}
.hf-ticket-dialog__close {
    display: grid;
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    color: #c6c8d0;
    border-radius: 6px;
    place-items: center;
}
.hf-ticket-dialog__close:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.08);
}
.hf-ticket-dialog__hero {
    margin: 17px 24px;
    padding: 14px 17px;
    background: linear-gradient(105deg, rgba(219, 81, 32, 0.17), transparent);
    border-left: 4px solid #f0784c;
}
.hf-ticket-dialog__status {
    padding: 7px 10px;
    color: #ffd5c4;
    background: rgba(219, 81, 32, 0.12);
    border: 1px solid rgba(240, 120, 76, 0.3);
    border-radius: 5px;
    font-size: 12px;
}
.hf-ticket-dialog__status--green {
    color: #b9e6c7;
    background: rgba(91, 170, 117, 0.12);
    border-color: rgba(91, 170, 117, 0.4);
}
.hf-ticket-dialog__status--neutral {
    color: #d4d5dc;
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.2);
}
.hf-ticket-dialog__scroll {
    min-height: 0;
    flex: 1 1 auto;
    overflow: auto;
    padding: 0 24px 24px;
    scrollbar-color: #f0784c #20222a;
    scrollbar-width: thin;
}
.hf-ticket-dialog__scroll::-webkit-scrollbar {
    width: 9px;
}
.hf-ticket-dialog__scroll::-webkit-scrollbar-track {
    background: #20222a;
}
.hf-ticket-dialog__scroll::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #f0784c, #c54820);
    border: 2px solid #20222a;
    border-radius: 9px;
}
.hf-ticket-dialog__facts {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 24px;
}
.hf-ticket-dialog__message {
    min-width: 0;
    padding: 16px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.14);
}
.hf-ticket-dialog dl {
    margin: 10px 0 0;
}
.hf-ticket-dialog__fact {
    display: grid;
    grid-template-columns: minmax(90px, 0.8fr) minmax(0, 1.2fr);
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    font-size: 12px;
}
.hf-ticket-dialog dt {
    color: #bfc1ca;
}
.hf-ticket-dialog dd {
    margin: 0;
    text-align: right;
}
.hf-ticket-dialog__message p {
    margin: 14px 0 0;
    font-size: 13px;
    line-height: 1.7;
    white-space: pre-wrap;
}
.hf-ticket-dialog__footer {
    justify-content: space-between;
    color: #c6c8d0;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    font-size: 12px;
}
.hf-ticket-dialog__action {
    min-height: 38px;
    padding: 0 14px;
    color: #fff;
    background: linear-gradient(135deg, #ed7042, #cb481e);
    border: 1px solid rgba(255, 177, 145, 0.38);
    border-radius: 6px;
    font: inherit;
    font-weight: 600;
}
.hf-ticket-dialog button:focus-visible,
.hf-ticket-dialog__scroll:focus-visible {
    outline: 2px solid #ff9a74;
    outline-offset: -3px;
}
@media (max-width: 960px) {
    .hf-ticket-dialog__overlay.v-dialog--fullscreen {
        height: 100%;
        max-height: 100% !important;
        margin: 0;
    }
    .hf-ticket-dialog__overlay.v-dialog--fullscreen .hf-ticket-dialog {
        border-radius: 0;
    }
}
@media (max-width: 600px) {
    .hf-ticket-dialog__intro,
    .hf-ticket-dialog__footer {
        padding: 16px;
    }
    .hf-ticket-dialog__mark {
        display: none;
    }
    .hf-ticket-dialog__hero {
        align-items: flex-start;
        flex-direction: column;
        margin: 16px;
    }
    .hf-ticket-dialog__scroll {
        padding: 0 16px 16px;
    }
    .hf-ticket-dialog__facts {
        grid-template-columns: minmax(0, 1fr);
    }
}
</style>
